import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  DialogActions, 
  TextField, 
  Box, 
  Button,
  Typography,
  Chip,
  Alert
} from "@mui/material";
import NotesIcon from '@mui/icons-material/Notes';
import axios from "axios";

/**
 * 備考欄専用の編集ダイアログ
 */
const NoteEditDialog = ({ open, onClose, client, onUpdate }) => {
  const [note, setNote] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const MAX_NOTE_LENGTH = 1000;

  // ダイアログが開かれた時に初期値を設定
  useEffect(() => {
    if (open && client) {
      const initialNote = client.note || "";
      setNote(initialNote);
      setHasChanges(false);
      setError("");
    }
  }, [open, client]);

  const handleNoteChange = (e) => {
    const newValue = e.target.value;
    setNote(newValue);
    setHasChanges(newValue !== (client?.note || ""));
    setError("");
  };

  const validateNote = (value) => {
    if (value.length > MAX_NOTE_LENGTH) {
      return `備考は${MAX_NOTE_LENGTH}文字以内で入力してください`;
    }
    
    // 基本的な禁止文字チェック
    if (value.match(/[<>"'`]/)) {
      return "備考に使用できない文字が含まれています（< > \" ' ` など）";
    }
    
    return "";
  };

  const handleSave = async () => {
    const validationError = validateNote(note);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    
    const token = sessionStorage.getItem("token");
    
    try {
      await axios.put(
        `/api/clients/${client.id}/note`,
        { note: note.trim() || null },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // 成功時の処理（メッセージなしで更新のみ）
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      if (err.response?.data && typeof err.response.data === "string") {
        setError(err.response.data);
      } else if (err.response?.status === 404) {
        setError("この顧客は削除されています");
      } else {
        setError("備考の更新に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm("変更内容が保存されていません。本当に閉じますか？");
      if (!confirmed) return;
    }
    onClose();
  };

  const remainingChars = MAX_NOTE_LENGTH - note.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <NotesIcon color="primary" />
          <Typography variant="h6">
            {client?.name} の備考編集
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          multiline
          rows={8}
          label="備考"
          value={note}
          onChange={handleNoteChange}
          placeholder="顧客に関するメモや特記事項を入力してください..."
          error={isOverLimit}
          helperText={
            <Box
              component="span"
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                width: '100%'
              }}
            >
              <span>
                {isOverLimit ? "文字数制限を超えています" : ""}
              </span>
              <Chip 
                component="span"
                label={`${note.length}/${MAX_NOTE_LENGTH}`}
                size="small"
                color={isOverLimit ? "error" : remainingChars < 100 ? "warning" : "default"}
                variant="outlined"
              />
            </Box>
          }
          sx={{
            '& .MuiInputBase-root': {
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.6
            }
          }}
        />

        {hasChanges && (
          <Box mt={1}>
            <Chip 
              label="未保存の変更あり" 
              size="small" 
              color="warning" 
              variant="outlined"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleCancel}
          disabled={loading}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || isOverLimit}
          sx={{ minWidth: 80 }}
        >
          {loading ? "保存中..." : "保存"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteEditDialog;